import { useEffect } from 'react';
import { registerCaptchaExecutor, TURNSTILE_SITE_KEY } from '@/lib/captcha/captcha-token';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

type Turnstile = {
  render: (el: HTMLElement, options: Record<string, unknown>) => string;
  execute: (id: string) => void;
  reset: (id: string) => void;
};

declare global {
  interface Window {
    turnstile?: Turnstile;
  }
}

let scriptPromise: Promise<Turnstile> | null = null;

function loadTurnstile(): Promise<Turnstile> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  scriptPromise ??= new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () =>
      window.turnstile ? resolve(window.turnstile) : reject(new Error('turnstile ausente'));
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error('falha ao carregar o turnstile'));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
}

/**
 * Widget do Turnstile fora da árvore do RN, preso no rodapé da tela. Em
 * `interaction-only` ele só aparece quando a Cloudflare pede um clique; no
 * resto das vezes o token sai sem o visitante ver nada.
 */
export function CaptchaHost() {
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;

    const container = document.createElement('div');
    container.setAttribute('data-captcha-host', '');
    Object.assign(container.style, {
      position: 'fixed',
      left: '50%',
      bottom: '16px',
      transform: 'translateX(-50%)',
      zIndex: '2147483000',
    });
    document.body.appendChild(container);

    let widgetId: string | null = null;
    let pending: { resolve: (t: string) => void; reject: (e: Error) => void } | null = null;

    const settle = (fn: (p: NonNullable<typeof pending>) => void) => {
      const current = pending;
      pending = null;
      if (current) fn(current);
    };

    const unregister = registerCaptchaExecutor(async () => {
      const turnstile = await loadTurnstile();
      if (!widgetId) {
        widgetId = turnstile.render(container, {
          sitekey: TURNSTILE_SITE_KEY,
          execution: 'execute',
          appearance: 'interaction-only',
          language: 'pt-br',
          callback: (token: string) => settle((p) => p.resolve(token)),
          'error-callback': (code: string) => {
            settle((p) => p.reject(new Error(`turnstile ${code}`)));
            // Devolve o controle ao widget sem ele tentar de novo sozinho.
            return true;
          },
          'expired-callback': () => settle((p) => p.reject(new Error('turnstile expired'))),
          'timeout-callback': () => settle((p) => p.reject(new Error('turnstile timeout'))),
        });
      } else {
        turnstile.reset(widgetId);
      }
      const id = widgetId;
      return new Promise<string>((resolve, reject) => {
        pending = { resolve, reject };
        turnstile.execute(id);
      });
    });

    return () => {
      unregister();
      settle((p) => p.reject(new Error('captcha host desmontado')));
      container.remove();
    };
  }, []);

  return null;
}
